import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ChatMessage } from '@aslgo/shared';
import { SendIcon, ChatIcon, CloseIcon } from '../icons.js';

interface Props {
  messages: ChatMessage[];
  /** Local user's auth uuid. Only ever compared against a message's `from`
   *  (both are uuids) to decide which side of the thread a bubble sits on. */
  selfUserId: string | null;
  /** Resolved display name of the other person in the call — the label on
   *  their bubbles. Null falls back to "Them"; a uuid must never be shown. */
  peerName: string | null;
  onSend(text: string): void;
  /** When true, the panel is rendered as a mobile drawer with a close button. */
  drawer?: boolean;
  onClose?: () => void;
}

const NEAR_BOTTOM_PX = 80;

/**
 * Always-mounted on desktop as a fixed-width right rail; mobile renders this
 * inside a slide-up drawer wrapper. Layout: header → message list (auto-scroll
 * to bottom unless the user scrolled up, in which case a "jump to latest"
 * pill appears) → composer.
 */
export function ChatPanel({ messages, selfUserId, peerName, onSend, drawer = false, onClose }: Props) {
  const [text, setText] = useState('');
  const [stuckToBottom, setStuckToBottom] = useState(true);
  const listRef = useRef<HTMLDivElement>(null);
  const lastCount = useRef(messages.length);

  function scrollToBottom(behavior: ScrollBehavior = 'auto') {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }

  // Track whether the user is "near bottom"; if so, auto-scroll. If they've
  // scrolled up to read history, leave them alone and surface the jump pill.
  function onScroll() {
    const el = listRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setStuckToBottom(distanceFromBottom <= NEAR_BOTTOM_PX);
  }

  useLayoutEffect(() => {
    if (lastCount.current !== messages.length) {
      const grew = messages.length > lastCount.current;
      lastCount.current = messages.length;
      if (grew && stuckToBottom) scrollToBottom('smooth');
    }
  }, [messages.length, stuckToBottom]);

  useEffect(() => {
    // First mount: pin to bottom without animation.
    scrollToBottom('auto');
    setStuckToBottom(true);
  }, []);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    onSend(text);
    setText('');
    setStuckToBottom(true);
    requestAnimationFrame(() => scrollToBottom('smooth'));
  }

  return (
    <aside
      className={`flex flex-col bg-surface-dark border-white/[0.06] text-ink-100
                  ${drawer ? 'h-full border-t' : 'h-full border-l'}`}
    >
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <ChatIcon className="text-ink-400" />
          <span className="text-[0.85rem] font-medium tracking-wide text-ink-200">Chat</span>
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat"
            className="p-1.5 rounded-md text-ink-400 hover:text-ink-100 hover:bg-white/5 transition"
          >
            <CloseIcon />
          </button>
        ) : null}
      </header>

      <div
        ref={listRef}
        onScroll={onScroll}
        className="relative flex-1 overflow-y-auto scroll-styled px-3 py-4 flex flex-col gap-3"
      >
        {messages.length === 0 ? (
          <p className="text-center text-[0.85rem] italic text-ink-500 mt-6">
            No messages yet — say hi.
          </p>
        ) : (
          messages.map((m, i) => {
            const mine = m.from === selfUserId;
            return (
              <div key={i} className={`flex flex-col max-w-[85%] ${mine ? 'self-end items-end' : 'self-start items-start'}`}>
                <span className="text-[0.65rem] uppercase tracking-[0.12em] text-ink-500 mb-0.5 px-0.5">
                  {mine ? 'You' : peerName ?? 'Them'}
                </span>
                <div
                  className={`px-3.5 py-2 rounded-2xl text-[0.95rem] leading-relaxed
                              ${mine
                                ? 'bg-brand-bright text-ink-900 rounded-br-sm'
                                : 'bg-white/[0.05] text-ink-100 rounded-bl-sm border border-white/5'}`}
                >
                  <MessageBody text={m.text} />
                </div>
              </div>
            );
          })
        )}
      </div>

      {!stuckToBottom && messages.length > 0 ? (
        <button
          type="button"
          onClick={() => { setStuckToBottom(true); scrollToBottom('smooth'); }}
          className="absolute right-4 bottom-24 px-3 py-1.5 rounded-full
                     bg-brand-bright text-ink-900 text-[0.75rem] font-medium shadow-chrome
                     animate-fade-in"
        >
          Jump to latest ↓
        </button>
      ) : null}

      <form
        onSubmit={submit}
        className="border-t border-white/[0.06] p-3 flex gap-2 items-end bg-canvas-dark/40"
      >
        <textarea
          rows={1}
          className="flex-1 resize-none px-3 py-2 rounded-lg bg-white/[0.05] border border-white/10
                     text-ink-100 placeholder:text-ink-500 text-[0.95rem] leading-snug
                     focus:outline-none focus:ring-2 focus:ring-brand-bright/40 focus:bg-white/[0.07]
                     scroll-styled"
          style={{ maxHeight: '8rem' }}
          placeholder="Type a message…"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            // Auto-grow the textarea up to maxHeight.
            const ta = e.currentTarget;
            ta.style.height = 'auto';
            ta.style.height = `${Math.min(ta.scrollHeight, 128)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit(e);
            }
          }}
        />
        <button
          type="submit"
          disabled={!text.trim()}
          aria-label="Send message"
          className="shrink-0 p-2.5 rounded-lg bg-brand-bright text-ink-900
                     hover:bg-brand transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <SendIcon />
        </button>
      </form>
    </aside>
  );
}

/**
 * Render bare URLs as tappable links (open in new tab). Anything else passes
 * through as text so emoji and unicode behave naturally.
 */
function MessageBody({ text }: { text: string }) {
  const parts = text.split(/(\bhttps?:\/\/\S+)/gi);
  return (
    <>
      {parts.map((part, i) =>
        /^https?:\/\//i.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 break-all hover:opacity-80"
          >
            {part}
          </a>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}
